/**
 * RadioGroup Component
 */

import React, { FC } from 'react';
import Handler from '../../commonTypes/handler.types';
import bindHandlers from '../../utils/handlerHelper';
import useControlledState from '../hooks/useControlledState/useControlledState';
import { RadioGroupProps, RadioButtonProps } from './radioGroup.types';
import {
  Label,
  HiddenRadioButton,
  RadioButton,
  Wrapper,
} from './radioGroup.style';

const RadioGroup: FC<RadioGroupProps> = (props: RadioGroupProps) => {

  const {
    data,
    name,
    checked,
    defaultChecked,
    handlers = {},
    disabled = false,
    className,
  } = props;

  const { onChange, ...otherHandlers } = handlers;

  // give user ability to make this fully controlled or uncontrolled based on requirements
  const [selectedState, setSelectedState] = useControlledState({
    controlled: checked !== undefined ? checked : undefined,
    default: defaultChecked || (checked === undefined && !disabled ? data[0].value : undefined),
    name: 'RadioGroup',
  });

  const internalHandlers: Handler = {
    onChange: (e: Event) => {
      const target = e && e.currentTarget as HTMLInputElement;
      const selectedValue = target && target.value;
      if (selectedValue) {
        setSelectedState(selectedValue);
        if (onChange) {
          onChange({
            selected: selectedValue,
            changes: selectedValue,
            elementId: name,
            event: 'select-option',
            nativeEvent: e,
          });
        }
      }
    }
  };

  const combinedHandlers: Handler = bindHandlers(
    null,
    otherHandlers,
    internalHandlers,
  );

  const radioButtons = data.map((radioButton: RadioButtonProps, index: number) => {

    const isChecked = !disabled && selectedState === radioButton.value;

    return (
      <Label key={index} disabled={disabled}>
        <HiddenRadioButton
          name={name}
          value={radioButton.value}
          checked={isChecked}
          disabled={disabled}
          aria-checked={isChecked}
          role="radio"
          tabIndex={isChecked ? 0 : -1}
          {...combinedHandlers}
        />
        <RadioButton />
        {radioButton.label}
      </Label>
    );
  });

  return (
    <Wrapper className={className} role="radiogroup">
      {radioButtons}
    </Wrapper>
  );
};

export default RadioGroup;
